import PropTypes from "prop-types";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { STATIC_ROOT } from "Config";
import { Input, InputNumber, Tooltip } from "top-component-library";
import { MAX_ORDER_AHEAD_HOURS, MIN_TIMEBLOCK_INTERVAL } from "../../constants";
import { hoursToMinutes, minutesToHours } from "../../scheduleUtils";
import {
  LabelWithTooltip,
  SettingControl,
  SettingRow,
  TimeblockDiagram,
  TimeblockLegendGroup,
  TimeblockSpacingLegend,
  TimeblockWidthLegend,
  TwoColumnRow,
} from "../../styles";

const HelpIcon = ({ title }) => (
  <Tooltip title={title}>
    <QuestionCircleOutlined style={{ padding: 4 }} />
  </Tooltip>
);

HelpIcon.propTypes = { title: PropTypes.string.isRequired };

// Section D: order-ahead timeblock config (left column) + required lead time
// (right column). The maximum lead time is stored in minutes but edited in
// hours (the legacy hoursformatter).
const AdvancedOrderAheadSection = ({ menu, onChange }) => {
  if (!menu.allow_order_ahead) {
    return (
      <p>
        <i>Enable &quot;This menu accepts future orders&quot; to configure order-ahead options.</i>
      </p>
    );
  }

  const maxHours = menu.max_order_ahead_minutes != null ? minutesToHours(menu.max_order_ahead_minutes) : undefined;

  return (
    <div>
      <p style={{ color: "#cf1322" }}>
        <i>
          Note: The timeblock width is added to the nominal pickup/delivery time. So if your last pickup/delivery time
          is 8pm, and timeblock width is 30 minutes, then the last timeblock displayed to guests will be 8-8:30pm.
        </i>
      </p>
      <TwoColumnRow>
        <div>
          <TimeblockLegendGroup>
            <TimeblockWidthLegend>Timeblock Width</TimeblockWidthLegend>
            <TimeblockDiagram>
              <TimeblockSpacingLegend>Timeblock Spacing</TimeblockSpacingLegend>
              <img
                src={`${STATIC_ROOT}img/timeblock_illustration.png`}
                alt="Diagram illustrating timeblock width and spacing"
                style={{ maxWidth: 400, marginBottom: 16, display: "block" }}
              />
            </TimeblockDiagram>
          </TimeblockLegendGroup>
          <SettingRow>
            <SettingControl>
              <label htmlFor={`${menu.menuId}-timepicker-title`}>
                <LabelWithTooltip>
                  <span>Timeblock title</span>
                  <HelpIcon title="What do guests see before checkout?" />
                </LabelWithTooltip>
              </label>
              <Input
                id={`${menu.menuId}-timepicker-title`}
                value={menu.timepicker_title ?? ""}
                onChange={(event) => onChange("timepicker_title", event.target.value)}
              />
            </SettingControl>
          </SettingRow>
          <SettingRow>
            <SettingControl>
              <label htmlFor={`${menu.menuId}-block-width`}>
                <LabelWithTooltip>
                  <span>Timeblock width (minutes)</span>
                  <HelpIcon title="The time width of the guests order ahead selection. E.g., 9-9:30pm or 9:30-10pm would be a 30 minute width." />
                </LabelWithTooltip>
              </label>
              <InputNumber
                id={`${menu.menuId}-block-width`}
                min={0}
                value={menu.timepicker_block_width}
                onChange={(value) => onChange("timepicker_block_width", value)}
              />
            </SettingControl>
          </SettingRow>
          <SettingRow>
            <SettingControl>
              <label htmlFor={`${menu.menuId}-block-interval`}>
                <LabelWithTooltip>
                  <span>Timeblock spacing (minutes)</span>
                  <HelpIcon title="The spacing between each timeblock." />
                </LabelWithTooltip>
              </label>
              <InputNumber
                id={`${menu.menuId}-block-interval`}
                min={MIN_TIMEBLOCK_INTERVAL}
                value={menu.timepicker_block_interval}
                onChange={(value) => onChange("timepicker_block_interval", value)}
              />
            </SettingControl>
          </SettingRow>
        </div>
        <div>
          <h4>Required lead time</h4>
          <p style={{ maxWidth: 350 }}>
            <small>
              Lead time refers to the time difference between when guests place an order, and when they want the order
              to be ready. The TOP by DoorDash checkout page will only let guests choose delivery/pickup times that meet
              all requirements below.
            </small>
          </p>
          <SettingRow>
            <SettingControl>
              <label htmlFor={`${menu.menuId}-min-days`}>
                <LabelWithTooltip>
                  <span>Minimum calendar days of lead time for delivery/pickup</span>
                  <HelpIcon title="The boundary of a day is considered to be 4am. For example, if you set this to 1 day, it means the cutoff for placing Friday lunch orders is 4am Friday morning." />
                </LabelWithTooltip>
              </label>
              <InputNumber
                id={`${menu.menuId}-min-days`}
                min={0}
                value={menu.min_order_ahead_days}
                onChange={(value) => onChange("min_order_ahead_days", value)}
              />
            </SettingControl>
          </SettingRow>
          <SettingRow>
            <SettingControl>
              <label htmlFor={`${menu.menuId}-min-minutes`}>
                <LabelWithTooltip>
                  <span>Minimum minutes of lead time for delivery/pickup</span>
                  <HelpIcon title="Controls how soon guests can request delivery/pickup, when placing an order. E.g. if this is 30 minutes, a guest ordering at 7pm will be required to select 7:30pm or later. If this is 0, and calendar days (above) is 0, guests can place 'ASAP' orders." />
                </LabelWithTooltip>
              </label>
              <InputNumber
                id={`${menu.menuId}-min-minutes`}
                min={0}
                value={menu.min_order_ahead_minutes}
                onChange={(value) => onChange("min_order_ahead_minutes", value)}
              />
            </SettingControl>
          </SettingRow>
          <SettingRow>
            <SettingControl>
              <label htmlFor={`${menu.menuId}-max-hours`}>
                <LabelWithTooltip>
                  <span>
                    <b>Maximum</b> allowed lead time for delivery/pickup (hours)
                  </span>
                  <HelpIcon title="Controls how far in the future guests can place orders for, in hours." />
                </LabelWithTooltip>
              </label>
              <InputNumber
                id={`${menu.menuId}-max-hours`}
                min={0}
                max={MAX_ORDER_AHEAD_HOURS}
                value={maxHours}
                onChange={(value) => onChange("max_order_ahead_minutes", hoursToMinutes(value ?? 0))}
              />
            </SettingControl>
          </SettingRow>
        </div>
      </TwoColumnRow>
    </div>
  );
};

AdvancedOrderAheadSection.propTypes = {
  menu: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AdvancedOrderAheadSection;
